import React from "react";
import { CREATION_OPTIONS } from './CreationOptionsGrid';
import { FaMagic } from 'react-icons/fa';

const InitialStepWorkflow = ({
  selectedCreationOption,
  onSelectCreationOption,
  contentGenerated,
  isGeneratingContent
}) => {
  // Don't render if content is already generated or currently generating
  if (contentGenerated || isGeneratingContent) {
    return null;
  }

  return (
    <div className="w-full px-3 py-3 sm:px-4 sm:py-4">
      <div className="w-full max-w-6xl mx-auto">
        
        {/* Header Section - ChatGPT Style */}
        <div className="text-center mb-3 sm:mb-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            <FaMagic className="text-pink-400 text-lg sm:text-xl animate-pulse" />
          </div>
          
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white tracking-tight leading-tight mb-1 sm:mb-2">
            What would you like to{' '}
            <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400 text-transparent bg-clip-text">
              create today?
            </span>
          </h1>
          
          <p className="text-gray-400 text-xs sm:text-sm max-w-2xl mx-auto">
            Choose from these presets to transform your marketing images
          </p>
        </div>

        {/* Options Grid - Circular Thumbnails */}
        <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3 mb-3 sm:mb-4">
          {CREATION_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => onSelectCreationOption(option.id)}
              className={`group relative flex flex-col items-center gap-1 sm:gap-2 p-2 rounded-xl transition-all duration-300 hover:scale-105 ${
                selectedCreationOption === option.id
                  ? 'bg-gradient-to-br from-pink-500/20 to-purple-500/20 border-2 border-pink-400/50 shadow-lg shadow-pink-400/25'
                  : 'bg-white/5 border-2 border-white/10 hover:border-white/30 hover:bg-white/10'
              }`}
              title={option.desc}
            >
              {/* Circular Image Container */}
              <div className={`relative w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full overflow-hidden ring-2 transition-all duration-300 ${
                selectedCreationOption === option.id
                  ? 'ring-pink-400 ring-offset-2 ring-offset-[#0a0a0a] shadow-xl shadow-pink-400/30'
                  : 'ring-white/20 group-hover:ring-white/40'
              }`}>
                <img 
                  src={option.img} 
                  alt={option.label} 
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                  loading="lazy"
                />
                
                {/* Selected Indicator */}
                {selectedCreationOption === option.id && (
                  <div className="absolute inset-0 bg-gradient-to-br from-pink-400/20 to-purple-400/20 flex items-center justify-center">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-pink-400 rounded-full flex items-center justify-center shadow-lg">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                )}

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>

              {/* Label */}
              <div className="text-center">
                <h3 className={`font-semibold text-[10px] sm:text-xs leading-tight transition-colors duration-300 ${
                  selectedCreationOption === option.id
                    ? 'text-pink-300'
                    : 'text-gray-200 group-hover:text-white'
                }`}>
                  {option.label}
                </h3>
              </div>

              {/* Glow Effect on Hover */}
              <div className={`absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                selectedCreationOption === option.id
                  ? 'bg-gradient-to-br from-pink-400/10 to-purple-400/10'
                  : 'bg-gradient-to-br from-white/5 to-transparent'
              }`} style={{ pointerEvents: 'none' }} />
            </button>
          ))}
        </div>

        {/* Helper Text - Compact */}
        <div className="text-center">
          {selectedCreationOption ? (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-400/30 rounded-full">
              <div className="w-1.5 h-1.5 bg-pink-400 rounded-full animate-pulse" />
              <span className="text-pink-300 text-[10px] sm:text-xs font-medium">
                {CREATION_OPTIONS.find(opt => opt.id === selectedCreationOption)?.label} selected
              </span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full">
              <FaMagic className="text-purple-400 text-xs" />
              <span className="text-gray-400 text-[10px] sm:text-xs">
                Select an action to get started
              </span>
            </div>
          )}
        </div>
        
      </div>
    </div>
  );
};

export default InitialStepWorkflow;
